import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { BusinessModuleRef } from '../../models/serializable/business-module-ref.js';
import { Building } from '../../models/serializable/seating/building.js';
import { LocalizedString } from '../../models/serializable/localized-string.js';
import { MapLocation } from '../../models/serializable/map-location.js';
import { Place, PlaceDisplayMode, PlaceStatus } from '../../models/serializable/place.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../../models/core/version-range.js';
import { Component } from '../core/component.js';

export class LocationComponent extends Component {
  static readonly TYPE_ID: string = '38d48f5c30744c1ba3b02051fa4799d9';
  static readonly SCHEMA_VERSION: number = 3;

  constructor(
    public readonly uniqueId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly names: LocalizedString,
    public readonly mapLocation: MapLocation,
    public readonly status: PlaceStatus,
    public readonly displayMode: PlaceDisplayMode,
    public readonly roomId: string | null,
    public readonly roomPublicName: LocalizedString | null,
  ) {
    super();
  }

  // For backward compatibility
  static fromPlace(place: Place): LocationComponent {
    return new LocationComponent(
        place.uniqueId,
        place.businessRef.clone(),
        place.names.clone(),
        place.mapLocation.clone(),
        place.status,
        place.displayMode,
        null,
        null,
    );
  }

  static fromBuilding(
      building: Building,
      options: {
        businessRef: BusinessModuleRef,
        roomId?: string | null,
        roomPublicName?: LocalizedString | null,
      },
  ): LocationComponent {
    return new LocationComponent(
        building.uniqueId,
        options.businessRef.clone(),
        building.names.clone(),
        building.location.clone(),
        PlaceStatus.Live,
        PlaceDisplayMode.Precise,
        options.roomId ?? null,
        options.roomPublicName?.clone() ?? null,
    );
  }

  get $removable(): boolean {
    return false;
  }

  get $typeId(): string {
    return LocationComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, LocationComponent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): LocationComponent {
    return new LocationComponent(
        MapUtils.get<string>(map, 'uniqueId'),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'names')),
        context.fromData<MapLocation>(MapUtils.get<SerializedData>(map, 'mapLocation')),
        toEnumFromString(MapUtils.get<string>(map, '$status'), PlaceStatus),
        toEnumFromString(MapUtils.get<string>(map, 'displayMode'), PlaceDisplayMode),
        MapUtils.getOrNull<string>(map, 'roomId'),
        context.fromDataOrNull<LocalizedString>(MapUtils.getOrNull<SerializedData>(map, 'roomPublicName')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'businessRef': this.businessRef.encode(),
      'names': context.toData(this.names),
      'mapLocation': context.toData(this.mapLocation),
      '$status': fromEnumAsString(this.status),
      'displayMode': fromEnumAsString(this.displayMode),
      'roomId': this.roomId,
      'roomPublicName': context.toDataOrNull(this.roomPublicName),
    };
  }

  clone(): this {
    return new LocationComponent(
        this.uniqueId,
        this.businessRef.clone(),
        this.names.clone(),
        this.mapLocation.clone(),
        this.status,
        this.displayMode,
        this.roomId,
        this.roomPublicName?.clone() ?? null,
    ) as this;
  }

  copyWith({
    uniqueId = this.uniqueId,
    businessRef = this.businessRef,
    names = this.names,
    mapLocation = this.mapLocation,
    status = this.status,
    displayMode = this.displayMode,
    roomId = this.roomId,
    roomPublicName = this.roomPublicName,
  }: {
    uniqueId?: string,
    businessRef?: BusinessModuleRef,
    names?: LocalizedString,
    mapLocation?: MapLocation,
    status?: PlaceStatus,
    displayMode?: PlaceDisplayMode,
    roomId?: string | null,
    roomPublicName?: LocalizedString | null,
  } = {}): this {
    return new LocationComponent(
        uniqueId,
        businessRef,
        names,
        mapLocation,
        status,
        displayMode,
        roomId,
        roomPublicName,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        isSameData(other.businessRef, this.businessRef) &&
        isSameData(other.names, this.names) &&
        isSameData(other.mapLocation, this.mapLocation) &&
        other.status === this.status &&
        other.displayMode === this.displayMode &&
        other.roomId === this.roomId &&
        isSameData(other.roomPublicName, this.roomPublicName);
  }
}

export class LocationComponentBuilder extends DataModelBuilder<LocationComponent> {
  build(context: SerializationContext, map: SerializedData): LocationComponent {
    return LocationComponent.fromMap(context, map);
  }

  override migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(LocationComponent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(LocationComponent.SCHEMA_VERSION)) {
      return null;
    }

    let version = versionRange.to;

    // In this version we added the `roomId` field of type `string | null`.
    if(version < 2) {
      map['roomId'] ??= null;
      version = 2;
    }

    // In this version we added the `roomPublicName` field of type `LocalizedString | null`.
    if(version < 3) {
      map['roomPublicName'] ??= null;
      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
